import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid2,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Schema } from 'yup';
import { array, boolean, object, string } from 'yup';

import Alert from '@components/Alert/Alert';
import MfaMethodList from '@components/ConfigOptionList/MfaMethodList';
import ContentLoader from '@components/ContentLoader';

import {
  useDisableMfa,
  useEnableMfa,
  useSetPreferredMfaMethod,
} from '@hooks/api/session';
import { useGetUserMfaSettings } from '@hooks/api/user';
import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import type { MfaFormValues } from '@models/mfa';

import { MfaMethod } from 'generated/types';

import VerifyAttribute from './VerifyAttribute';

const messages = defineMessages({
  title: {
    id: 'changeMfa.title',
    defaultMessage: 'Multi-Factor Authentication',
  },
  preferredMethod: {
    id: 'changeMfa.preferredMethod',
    defaultMessage: 'Preferred MFA Method',
  },
  information: {
    id: 'changeMfa.information',
    defaultMessage:
      'Multi-factor authentication is a feature for added account security. When enabled, there will be two steps when logging into your account: Entering your username and password, followed by entering a code sent to your phone, email, or third-party authenticator app.',
  },
  methods: {
    id: 'changeMfa.methods',
    defaultMessage: 'MFA Methods',
  },
  codeRequired: {
    id: 'changeMfa.codeRequired',
    defaultMessage: 'Authentication code is required',
  },
  error: {
    id: 'changeMfa.error',
    defaultMessage:
      '{text} Please try to {configure} MFA again with a valid code.',
  },
  smsMfa_title: {
    defaultMessage: 'SMS',
    id: 'changeMfa.sms.title',
  },
  totpMfa_title: {
    defaultMessage: 'Authenticator App',
    id: 'changeMfa.totp.title',
  },
  emailMfa_title: {
    defaultMessage: 'Email',
    id: 'changeMfa.email.title',
  },
  emailNoPhoneWarningTitle: {
    id: 'mfaSettings.emailNoPhoneWarningTitle',
    defaultMessage: 'Password reset requires support',
  },
  emailNoPhoneWarningBody: {
    id: 'mfaSettings.emailNoPhoneWarningBody',
    defaultMessage:
      'If you use email to receive your sign-in code, you cannot also use it to reset your password.\n\nA phone number is not on file, which means you will need to contact support to reset your password. Add a phone number to your account or choose a different MFA method to reset it on your own.',
  },
  emailNoPhoneAcknowledge: {
    id: 'mfaSettings.emailNoPhoneAcknowledge',
    defaultMessage:
      "I understand I'll need to contact support to reset my password.",
  },
  preferredUpdated: {
    id: 'changeMfa.preferredUpdated',
    defaultMessage: 'Preferred MFA method updated.',
  },
  expiredCode: {
    id: 'GLOBAL.expiredCode',
    defaultMessage: 'Code expired.',
  },
  incorrectCode: {
    id: 'GLOBAL.incorrectCode',
    defaultMessage: 'Incorrect code.',
  },
  globalError: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
});

const ChangeMfa: React.FC = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { activeProfile, active_user_id, logged_in_user_id },
  } = useSession();
  const { phoneSchema } = useCommonSchemas();

  const { palette } = useTheme();

  const [enabling, setEnabling] = useState<boolean | null>(null);

  const { data: mfaPreference, loading: mfaPreferenceLoading } =
    useGetUserMfaSettings();
  const loading = mfaPreferenceLoading;

  const mfaResponse = mfaPreference?.getUserRecord.mfa;
  const rawPreferredMethod = mfaResponse?.status ?? MfaMethod.NONE;
  // When status is NONE but methods are configured, default to the first configured method
  const preferredMfaMethod =
    rawPreferredMethod === MfaMethod.NONE
      ? mfaResponse?.smsEnabled
        ? MfaMethod.SMS_MFA
        : mfaResponse?.totpEnabled
          ? MfaMethod.SOFTWARE_TOKEN_MFA
          : mfaResponse?.emailEnabled
            ? MfaMethod.EMAIL_OTP
            : MfaMethod.NONE
      : rawPreferredMethod;
  const [smsConfigured, totpConfigured, emailConfigured] = [
    Boolean(mfaResponse?.smsEnabled),
    Boolean(mfaResponse?.totpEnabled),
    Boolean(mfaResponse?.emailEnabled),
  ];
  const [acknowledgedNoPhone, setAcknowledgedNoPhone] = useState(false);
  const phoneVerified = Boolean(mfaResponse?.phoneVerified);
  const phoneNumber = mfaResponse?.phone_number;

  const [enableMfa] = useEnableMfa();
  const [disableMfa] = useDisableMfa();
  const [setPreferredMfaMethod] = useSetPreferredMfaMethod();

  const initialValues: MfaFormValues = {
    method: null,
    code: ['', '', '', '', '', ''],
    phone: mfaResponse?.phone_number ?? '',
    preferredMethod: null,
    submit: null,
  };

  const validationSchema: Schema<Partial<MfaFormValues>> = object().shape({
    method: string()
      .oneOf([
        MfaMethod.SOFTWARE_TOKEN_MFA,
        MfaMethod.SMS_MFA,
        MfaMethod.EMAIL_OTP,
      ])
      .nullable(),
    code: array().when('method', {
      is: MfaMethod.EMAIL_OTP,
      then: (schema) => schema.of(string().max(1)),
      otherwise: (schema) =>
        schema
          .of(string().max(1).required(formatMessage(messages.codeRequired)))
          .required(formatMessage(messages.codeRequired)),
    }),
    phone: phoneSchema,
    preferredMethod: boolean().nullable(),
  });

  const MfaMethodMap: Partial<Record<MfaMethod, { display: string }>> = {
    [MfaMethod.SMS_MFA]: {
      display: formatMessage(messages.smsMfa_title),
    },
    [MfaMethod.SOFTWARE_TOKEN_MFA]: {
      display: formatMessage(messages.totpMfa_title),
    },
    [MfaMethod.EMAIL_OTP]: {
      display: formatMessage(messages.emailMfa_title),
    },
  };

  // Build dropdown options — only configured methods
  const preferredOptions: { k: string; v: string }[] = [
    ...(smsConfigured
      ? [{ k: MfaMethod.SMS_MFA, v: MfaMethodMap[MfaMethod.SMS_MFA]!.display }]
      : []),
    ...(totpConfigured
      ? [
          {
            k: MfaMethod.SOFTWARE_TOKEN_MFA,
            v: MfaMethodMap[MfaMethod.SOFTWARE_TOKEN_MFA]!.display,
          },
        ]
      : []),
    ...(emailConfigured
      ? [
          {
            k: MfaMethod.EMAIL_OTP,
            v: MfaMethodMap[MfaMethod.EMAIL_OTP]!.display,
          },
        ]
      : []),
  ];

  const handlePreferredChange = async (event: SelectChangeEvent) => {
    const method = event.target.value as MfaMethod;
    const { errors } = await setPreferredMfaMethod({
      input: { preferredMethod: method },
    });
    if (!errors) {
      enqueueSnackbar({
        variant: 'success',
        message: formatMessage(messages.preferredUpdated),
      });
    }
  };

  async function handleSubmit(
    values: MfaFormValues,
    { resetForm }: FormikHelpers<MfaFormValues>
  ) {
    const email = activeProfile?.email;
    const { code, method, preferredMethod } = values;

    if (!email || !method) {
      throw new Error(
        'Either no email and/or tokens were provided or a valid MFA method was not selected'
      );
    }

    const getErrorText = (message: string) =>
      message.startsWith('CodeMismatchException') ||
      message.startsWith('EnableSoftwareTokenMFAException')
        ? formatMessage(messages.incorrectCode)
        : message.startsWith('ExpiredCodeException')
          ? formatMessage(messages.expiredCode)
          : '';

    try {
      if (enabling) {
        const isEmailOtp = method === MfaMethod.EMAIL_OTP;
        const noMethodsConfigured =
          !smsConfigured && !totpConfigured && !emailConfigured;
        const { data, errors } = await enableMfa({
          input: {
            ...(!isEmailOtp && { code: code.join('') }),
            method,
            preferredMethod: noMethodsConfigured
              ? method
              : preferredMethod
                ? method
                : preferredMfaMethod,
          },
        });
        const message = data?.enableMfa.message;
        const errorCode = errors?.at(0)?.message;
        const text = getErrorText(errorCode ?? '');

        enqueueSnackbar({
          variant: message ? 'success' : 'error',
          message:
            message ??
            formatMessage(messages.error, {
              text,
              configure: 'enable',
            }),
        });
      } else {
        const { data, errors } = await disableMfa({
          input: {
            code: code.join(''),
            method,
          },
        });
        const message = data?.disableMfa.message;
        const errorCode = errors?.at(0)?.message;
        const text = getErrorText(errorCode ?? '');

        enqueueSnackbar({
          variant: message ? 'success' : 'error',
          message:
            message ??
            formatMessage(messages.error, {
              text,
              configure: 'disable',
            }),
        });
      }
    } catch (err) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.globalError),
      });
    } finally {
      await resetForm({ values: initialValues });
      setEnabling(null);
    }
  }

  return (
    <Grid2 container>
      <ContentLoader loading={loading}>
        {/* Preferred MFA dropdown */}
        {preferredOptions.length >= 1 && (
          <Grid2 size={{ xs: 12 }} mb={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" fontWeight={600} noWrap>
                {formatMessage(messages.preferredMethod)}:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={preferredMfaMethod}
                  onChange={handlePreferredChange}
                >
                  {preferredOptions.map((opt) => (
                    <MenuItem key={opt.k} value={opt.k}>
                      {opt.v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Grid2>
        )}

        <Grid2 size={{ xs: 12 }} mb={1}>
          <Typography color="text.secondary" variant="body2">
            {formatMessage(messages.information)}
          </Typography>
        </Grid2>
      </ContentLoader>

      {logged_in_user_id === active_user_id ? (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ setFieldValue, values }) => {
            const handleMethodClick = (value: string) => {
              const isEnabling =
                (value === MfaMethod.SMS_MFA && !smsConfigured) ||
                (value === MfaMethod.SOFTWARE_TOKEN_MFA && !totpConfigured) ||
                (value === MfaMethod.EMAIL_OTP && !emailConfigured);
              setEnabling(isEnabling);
              setAcknowledgedNoPhone(false);
              setFieldValue('method', value);
              const noMethodsConfigured =
                !smsConfigured && !totpConfigured && !emailConfigured;
              if (noMethodsConfigured) {
                setFieldValue('preferredMethod', value === preferredMfaMethod);
              }
            };

            return (
              <Form>
                <FormLabel sx={{ color: palette.text.primary, fontSize: 15 }}>
                  {formatMessage(messages.methods)}
                </FormLabel>
                <MfaMethodList
                  config={{
                    showConfig: true,
                    smsConfigured,
                    totpConfigured,
                    emailConfigured,
                  }}
                  extraContent={{
                    [MfaMethod.EMAIL_OTP]:
                      values.method === MfaMethod.EMAIL_OTP &&
                      enabling &&
                      !phoneVerified ? (
                        <Alert
                          severity="warning"
                          title={formatMessage(
                            messages.emailNoPhoneWarningTitle
                          )}
                          sx={{ m: 1, mt: 0 }}
                        >
                          <Typography variant="caption" whiteSpace="pre-line">
                            {formatMessage(messages.emailNoPhoneWarningBody)}
                          </Typography>
                          <FormControlLabel
                            sx={{
                              mt: 1,
                              alignItems: 'flex-start',
                              ml: 0,
                              gap: 0.5,
                            }}
                            control={
                              <Checkbox
                                checked={acknowledgedNoPhone}
                                onChange={(e) =>
                                  setAcknowledgedNoPhone(e.target.checked)
                                }
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                sx={{ p: 0, mt: '-1.5px' }}
                              />
                            }
                            label={
                              <Typography variant="caption" lineHeight={1.4}>
                                {formatMessage(
                                  messages.emailNoPhoneAcknowledge
                                )}
                              </Typography>
                            }
                          />
                        </Alert>
                      ) : undefined,
                  }}
                  method={values.method ?? ''}
                  onClick={(value) => handleMethodClick(value)}
                />

                {values.method &&
                typeof enabling === 'boolean' &&
                !(
                  values.method === MfaMethod.EMAIL_OTP &&
                  enabling &&
                  !phoneVerified &&
                  !acknowledgedNoPhone
                ) ? (
                  <VerifyAttribute
                    enable={enabling}
                    smsConfigured={smsConfigured}
                    totpConfigured={totpConfigured}
                    emailConfigured={emailConfigured}
                    phoneNumber={phoneNumber}
                  />
                ) : null}
              </Form>
            );
          }}
        </Formik>
      ) : null}
    </Grid2>
  );
};

export default ChangeMfa;
